import { Component, OnInit } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PASSWORD_LENGTH_LIMIT } from '@zlib/data-access/constants';
import { ChangePassword } from '@zlib/data-access/models/user.model';
import { AuthService } from '@zlib/services/auth.service';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    imports: [
        TranslateModule,
        NzIconModule,
        NzFormModule,
        FormsModule,
        NzInputModule,
        ReactiveFormsModule
    ]
})
export class ChangePasswordComponent implements OnInit {
    validateNewPasswordForm!: UntypedFormGroup;
    newPasswordVisible = false;
    confirmNewPasswordVisible = false;
    oldPasswordVisible = false;
    changePasswordModel: ChangePassword = {
        oldPassword: '',
        newPassword: ''
    };

    constructor(
        private formbuilder: UntypedFormBuilder,
        private authService: AuthService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.validateNewPasswordForm = this.formbuilder.group({
            oldPassword: [null, [Validators.required]],
            newPassword: [null, [Validators.required, this.strongPassword]],
            confirmNewPassword: [
                null,
                [Validators.required, this.passwordConfirmValidator]
            ]
        });

        this.authService.currentUserSubject.subscribe((user) => {
            this.changePasswordModel.username = user.username;
        });
    }

    strongPassword = (
        control: UntypedFormControl
    ): { [s: string]: boolean } => {
        const lengthRequired =
            (control?.value?.length ?? 0) >= PASSWORD_LENGTH_LIMIT;
        if (!control.value) {
            return { required: true };
        } else if (!lengthRequired) {
            return { length: true };
        }
        return {};
    };

    passwordConfirmValidator = (
        control: UntypedFormControl
    ): { [s: string]: boolean } => {
        if (!control.value) {
            return { required: true };
        } else if (
            control.value !==
            this.validateNewPasswordForm.controls['newPassword'].value
        ) {
            return { confirm: true, error: true };
        }
        return {};
    };

    clearPassword(): void {
        this.validateNewPasswordForm.reset();
    }

    onConfirmNewPassword(): void {
        if (this.validateNewPasswordForm.valid) {
            const {
                oldPassword,
                newPassword
            }: { oldPassword: string; newPassword: string } =
                this.validateNewPasswordForm.value;
            this.changePasswordModel.newPassword = newPassword;
            this.changePasswordModel.oldPassword = oldPassword;
            this.authService
                .changePassword(this.changePasswordModel)
                .subscribe((res) => {
                    if (res) {
                        this.authService.logout().subscribe(() => {
                            this.router.navigate(['/auth/login']);
                        });
                    }
                });
        } else {
            this.validateForm(this.validateNewPasswordForm);
        }
    }

    validateForm(formGroup: UntypedFormGroup): void {
        Object.values(formGroup.controls).forEach((control) => {
            if (control.invalid) {
                control.markAsDirty();
                control.updateValueAndValidity({ onlySelf: true });
            }
        });
    }
}
